import { FormArray, FormGroup } from '@angular/forms';

import { WorkflowsHelperService } from '../../services/workflows-helper.service';
import {
	extractWorkflowRichTextHtml,
	hasWorkflowLocaleText,
	serializeWorkflowLocaleFormValue,
	trimmedLocaleTextRequiredValidator,
	trimmedRichTextLocaleRequiredValidator,
} from '../../utils/workflow-locale-text.util';
import { IFormFieldPreset } from './form-field-presets';

/** Presets cuyas opciones editables usan índice de orden (1-based), no el texto del label i18n. */
export const ORDER_INDEXED_FORM_STEP_OPTION_FIELD_IDS = new Set(['multi_select', 'single_select', 'checkbox', 'legal']);

/** Valor estable de opción según posición en la lista (mismo en todos los idiomas). */
export function resolveFormStepOptionOrderValue(optionIndex: number): string {
	return String(optionIndex + 1);
}

export function usesOrderIndexedFormStepOptionValues(field: Record<string, unknown>): boolean {
	const fieldId = String(field['id'] ?? field['name'] ?? '');
	return ORDER_INDEXED_FORM_STEP_OPTION_FIELD_IDS.has(fieldId);
}

export function assignFormStepOptionOrderValues(options: FormArray): void {
	options.controls.forEach((control, index) => {
		const existing = String((control as FormGroup).get('value')?.value ?? '').trim();
		if (existing === 'yes' || existing === 'no') {
			return;
		}

		(control as FormGroup).get('value')?.setValue(resolveFormStepOptionOrderValue(index), { emitEvent: false });
	});
}

function isLocaleLabelMap(value: unknown): value is Record<string, unknown> {
	return Boolean(value && typeof value === 'object' && !Array.isArray(value) && !('html' in value));
}

function extractRichTextHtml(label: unknown): string {
	return extractWorkflowRichTextHtml(label);
}

export function getFieldOptionsArray(fieldsArray: FormArray, fieldIndex: number): FormArray {
	return fieldsArray.at(fieldIndex).get('options') as FormArray;
}

export function createFormStepOptionGroup(workflowsHelper: WorkflowsHelperService): FormGroup {
	return workflowsHelper.createFormFieldOptionGroup(false);
}

export function createFormStepRichTextOptionGroup(workflowsHelper: WorkflowsHelperService): FormGroup {
	return workflowsHelper.createFormFieldOptionGroup(true);
}

export function seedFormStepDefaultOptions(
	options: FormArray,
	workflowsHelper: WorkflowsHelperService,
	count: number,
	richText = false,
): void {
	const createGroup = richText ? createFormStepRichTextOptionGroup : createFormStepOptionGroup;

	for (let index = 0; index < count; index += 1) {
		const optionGroup = createGroup(workflowsHelper);
		optionGroup.get('value')?.setValue(resolveFormStepOptionOrderValue(index));
		options.push(optionGroup);
	}
}

export function seedFormStepYesNoOptions(
	options: FormArray,
	workflowsHelper: WorkflowsHelperService,
	yesLabel: string,
	noLabel: string,
): void {
	options.clear();

	[
		{ value: 'yes', label: yesLabel },
		{ value: 'no', label: noLabel },
	].forEach((option) => {
		const optionGroup = workflowsHelper.createFormFieldOptionGroup(false);
		const labelGroup = optionGroup.get('label') as FormGroup;

		Object.keys(labelGroup.controls).forEach((locale) => {
			labelGroup.get(locale)?.setValue(option.label);
		});

		optionGroup.get('value')?.setValue(option.value);
		options.push(optionGroup);
	});
}

function setOptionLabelValidators(labelGroup: FormGroup | null | undefined, richText: boolean): void {
	if (!labelGroup) {
		return;
	}

	const validators = richText ? [trimmedRichTextLocaleRequiredValidator()] : [trimmedLocaleTextRequiredValidator()];

	Object.values(labelGroup.controls).forEach((control) => {
		control.setValidators(validators);
		control.updateValueAndValidity({ emitEvent: false });
	});
	labelGroup.updateValueAndValidity({ emitEvent: false });
}

function clearOptionLabelValidators(labelGroup: FormGroup | null | undefined): void {
	if (!labelGroup) {
		return;
	}

	Object.values(labelGroup.controls).forEach((control) => {
		control.clearValidators();
		control.updateValueAndValidity({ emitEvent: false });
	});
	labelGroup.updateValueAndValidity({ emitEvent: false });
}

/** Activa obligatoriedad multi-idioma en labels de opciones según preset (select/checkbox/legal). */
export function applyFormStepOptionsValidators(
	fieldsArray: FormArray,
	workflowsHelper: WorkflowsHelperService,
	preset?: IFormFieldPreset | null,
): void {
	if (!preset?.usesOptionsEditor && !preset?.usesRichTextOptionsEditor && !preset?.usesLegalFieldsConfig) {
		return;
	}

	const richText = Boolean(preset.usesRichTextOptionsEditor || preset.usesLegalFieldsConfig);

	fieldsArray.controls.forEach((fieldControl) => {
		const options = (fieldControl as FormGroup).get('options') as FormArray;

		options?.controls.forEach((optionControl) => {
			setOptionLabelValidators((optionControl as FormGroup).get('label') as FormGroup, richText);
		});
	});
}

export function sanitizeLockedPresetFieldsValidation(fieldsArray: FormArray): void {
	fieldsArray.controls.forEach((control) => {
		const options = (control as FormGroup).get('options') as FormArray;

		options?.controls.forEach((option) => {
			clearOptionLabelValidators((option as FormGroup).get('label') as FormGroup);
		});
	});
}

export function markFormStepOptionsTouched(fieldsArray: FormArray): void {
	fieldsArray.controls.forEach((fieldControl) => {
		const options = (fieldControl as FormGroup).get('options') as FormArray;
		options?.markAllAsTouched();
	});
}

/** Primera opción inválida: idioma y índice de fila para pestaña + autofocus. */
export function firstInvalidFormStepOptionRef(
	fieldsArray: FormArray,
	allowedLocales: readonly string[],
	richText: boolean,
): { localeId: string; optionIndex: number } | null {
	for (const fieldControl of fieldsArray.controls) {
		const options = (fieldControl as FormGroup).get('options') as FormArray;

		for (let optionIndex = 0; optionIndex < (options?.length ?? 0); optionIndex += 1) {
			const labelGroup = (options.at(optionIndex) as FormGroup).get('label') as FormGroup;

			for (const locale of allowedLocales) {
				const control = labelGroup?.get(locale);
				if (!control) {
					continue;
				}

				if (control.invalid) {
					return { localeId: locale, optionIndex };
				}

				const hasText = richText
					? extractRichTextHtml(control.value).length > 0
					: (control.value ?? '').toString().trim().length > 0;

				if (!hasText) {
					return { localeId: locale, optionIndex };
				}
			}
		}
	}

	return null;
}

export function addFormStepOption(
	fieldsArray: FormArray,
	fieldIndex: number,
	workflowsHelper: WorkflowsHelperService,
): void {
	const options = getFieldOptionsArray(fieldsArray, fieldIndex);
	const optionGroup = createFormStepOptionGroup(workflowsHelper);
	optionGroup.get('value')?.setValue(resolveFormStepOptionOrderValue(options.length));
	options.push(optionGroup);
}

export function removeFormStepOption(fieldsArray: FormArray, fieldIndex: number, optionIndex: number): void {
	const options = getFieldOptionsArray(fieldsArray, fieldIndex);

	if (options.length <= 1) {
		return;
	}

	options.removeAt(optionIndex);
	assignFormStepOptionOrderValues(options);
}

export function normalizeFormStepFieldOptions(
	field: Record<string, unknown>,
	allowedLocales: readonly string[],
): Record<string, unknown> {
	const options = ((field['options'] as { value?: string; label?: unknown }[]) ?? [])
		.map((option, index) => {
			const labelMap = isLocaleLabelMap(option.label)
				? serializeWorkflowLocaleFormValue(option.label, allowedLocales)
				: normalizePlainOptionLabel(option.label, allowedLocales);

			return {
				value: resolveFormStepOptionOrderValue(index),
				label: labelMap,
			};
		})
		.filter((option) => hasWorkflowLocaleText(option.label));

	return {
		...field,
		options,
	};
}

export function normalizeFormStepRichTextFieldOptions(
	field: Record<string, unknown>,
	allowedLocales: readonly string[],
): Record<string, unknown> {
	const options = ((field['options'] as { value?: string; label?: unknown }[]) ?? [])
		.map((option, index) => {
			const labelMap = normalizeRichTextOptionLabel(option.label, allowedLocales);

			return {
				value: resolveFormStepOptionOrderValue(index),
				label: labelMap,
			};
		})
		.filter((option) => hasWorkflowLocaleText(option.label));

	return {
		...field,
		multiple: true,
		options,
	};
}

export function patchRichTextOptionLabels(options: FormArray): void {
	options.controls.forEach((control) => {
		const labelGroup = (control as FormGroup).get('label') as FormGroup;

		Object.keys(labelGroup.controls).forEach((locale) => {
			const localeControl = labelGroup.get(locale);
			if (!localeControl) return;

			const html = extractWorkflowRichTextHtml(localeControl.value);
			if (typeof localeControl.value === 'string' && localeControl.value === html) {
				return;
			}

			localeControl.setValue(html, { emitEvent: false });
		});
	});
}

function normalizePlainOptionLabel(label: unknown, allowedLocales: readonly string[]): Record<string, string> {
	const trimmed = String(label ?? '').trim();
	const out = Object.fromEntries(allowedLocales.map((locale) => [locale, ''])) as Record<string, string>;

	if (trimmed) {
		for (const locale of allowedLocales) {
			out[locale] = trimmed;
		}
	}

	return out;
}

function normalizeRichTextOptionLabel(label: unknown, allowedLocales: readonly string[]): Record<string, string> {
	const out = Object.fromEntries(allowedLocales.map((locale) => [locale, ''])) as Record<string, string>;

	if (isLocaleLabelMap(label)) {
		for (const locale of allowedLocales) {
			out[locale] = extractRichTextHtml(label[locale]);
		}
		return out;
	}

	const trimmed = extractRichTextHtml(label);
	if (trimmed) {
		for (const locale of allowedLocales) {
			out[locale] = trimmed;
		}
	}

	return out;
}
